import { z } from 'zod';
import { messageSchema } from './message.js';
/**
 * Kereses-ertelmezo.
 *
 * A modositok (`from:`, `in:`, `has:`) ugyanazok, mint a Slackben - aki onnan
 * jon, ne kelljen ujratanulnia. Ami nem modosito, az szabad szoveg.
 *
 * Az ertelmezes SZANDEKOSAN a kozos csomagban van: a kliens ebbol tudja
 * megmutatni, mit ertett a keresesbol, es a szerver ugyanezt hasznalja.
 * Ket kulon ertelmezo elobb-utobb elterne egymastol.
 */
export const searchFilterSchema = z.object({
    /** A szabad szoveges resz. */
    text: z.string(),
    /** Szerzok - nev vagy e-mail toredeke. */
    from: z.array(z.string()),
    /** Csatornak - nev toredeke. */
    in: z.array(z.string()),
    /** `file` vagy `link`. */
    has: z.array(z.enum(['file', 'link'])),
    /** `saved`, `pinned` vagy `important`. */
    is: z.array(z.enum(['saved', 'pinned', 'important'])),
    /**
     * Emlitesek szurese.
     *
     * `me`  - amiben ENGEM emlitenek. Nem csak a nevre szolo emlites szamit: a
     *         `@channel`, a `@here` es a sajat csoportjaim emlitese is nekem
     *         szol. Ugyanaz a jelentes, mint amitol ertesitest kapok.
     * `any` - amiben BARKIT emlitenek. Ez mas kerdesre valaszol: nem azt, hogy
     *         "mi tartozik ram", hanem hogy "hol osztottak ki valamit".
     */
    mentions: z.enum(['me', 'any']).nullable(),
    before: z.string().nullable(),
    after: z.string().nullable(),
});
const EMPTY = {
    text: '',
    from: [],
    in: [],
    has: [],
    is: [],
    mentions: null,
    before: null,
    after: null,
};
/** Csak a nap pontossagu datumot fogadjuk el; a napszak itt zajt vinne be. */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export function parseSearchQuery(raw) {
    const result = { ...EMPTY, from: [], in: [], has: [], is: [] };
    const words = [];
    // Idezojelbe tett resz egyben marad, a tobbi szokozzel valik el.
    const tokens = raw.match(/"[^"]*"|\S+/g) ?? [];
    for (const token of tokens) {
        const match = /^(from|in|has|is|mentions|before|after):(.*)$/i.exec(token);
        if (!match) {
            words.push(token.replace(/^"|"$/g, ''));
            continue;
        }
        const key = match[1].toLowerCase();
        const value = match[2].replace(/^"|"$/g, '').replace(/^[@#]/, '').trim();
        if (!value)
            continue;
        switch (key) {
            case 'from':
                result.from.push(value);
                break;
            case 'in':
                result.in.push(value);
                break;
            case 'has':
                if (value === 'file' || value === 'link')
                    result.has.push(value);
                break;
            case 'is':
                if (value === 'saved' || value === 'pinned' || value === 'important')
                    result.is.push(value);
                break;
            /*
              A magyar alakokat is elfogadjuk.
      
              A tobbi modosito a Slackbol jon, azokat sokan izommemoriabol irjak -
              ez viszont a mi sajatunk, es a felulet magyar. Aki a gomb feliratat
              latta ("engem emlitenek"), az ne csodalkozzon, ha begepelve nem
              mukodik ugyanaz a szo.
            */
            case 'mentions':
                if (value === 'me' || value === 'engem')
                    result.mentions = 'me';
                if (value === 'any' || value === 'all' || value === 'mind')
                    result.mentions = 'any';
                break;
            case 'before':
                if (DATE_RE.test(value))
                    result.before = value;
                break;
            case 'after':
                if (DATE_RE.test(value))
                    result.after = value;
                break;
        }
    }
    result.text = words.join(' ').trim();
    return result;
}
/** Van-e egyaltalan mit keresni? Ures szuro nem inditson lekerdezest. */
export const isEmptyFilter = (filter) => !filter.text &&
    filter.from.length === 0 &&
    filter.in.length === 0 &&
    filter.has.length === 0 &&
    filter.is.length === 0 &&
    !filter.mentions &&
    !filter.before &&
    !filter.after;
// ─── Talalatok ────────────────────────────────────────────────────────────────
export const searchHitSchema = z.object({
    message: messageSchema,
    /** A talalat kiemelt reszlete (a keresett szavak koruli szovegkornyezet). */
    excerpt: z.string(),
    channelName: z.string().nullable(),
    channelKind: z.string(),
});
/**
 * VASZON-talalat - a keresooldal kulon szakasza.
 *
 * Nem uzenet: nincs szerzoje, sorszama, idopontja helyett az utolso
 * modositasa van. Ezert kulon alak, nem a `SearchHit` kiterjesztese.
 */
export const canvasSearchHitSchema = z.object({
    canvasId: z.string(),
    channelId: z.string(),
    channelName: z.string().nullable(),
    channelKind: z.string(),
    title: z.string(),
    /** A kiemelt reszlet, `<<...>>` jelolokkel - mint az uzenetnel. */
    excerpt: z.string(),
    updatedAt: z.string(),
});
export const searchQueryInput = z.object({
    q: z.string().min(1).max(400),
    /**
     * Egyetlen csatornara szukites - a CSATORNAN BELULI keresohoz.
     *
     * Az `in:#nev` nevre illeszt, a DM-nek viszont nincs neve. Az azonosito
     * minden fajtanal mukodik, es pontosan egy beszelgetest jelol ki.
     */
    channelId: z.string().optional(),
    sort: z.enum(['relevance', 'newest']).default('relevance'),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    offset: z.coerce.number().int().min(0).max(1000).default(0),
});
export const SEARCH_MODIFIER_HELP = [
    { syntax: 'from:@név', meaning: 'egy embertol' },
    { syntax: 'in:#csatorna', meaning: 'egy csatornában' },
    { syntax: 'has:file', meaning: 'csak melléklettel' },
    { syntax: 'has:link', meaning: 'csak linkkel' },
    { syntax: 'is:saved', meaning: 'a mentettek között' },
    { syntax: 'is:pinned', meaning: 'a kitűzöttek között' },
    { syntax: 'is:important', meaning: 'csak a fontosnak jelölt üzenetek' },
    { syntax: 'mentions:me', meaning: 'amiben engem említenek' },
    { syntax: 'mentions:any', meaning: 'amiben bárkit említenek' },
    { syntax: 'before:2026-01-01', meaning: 'ez előtt' },
    { syntax: 'after:2026-01-01', meaning: 'ez után' },
];
//# sourceMappingURL=search.js.map